import { FC, useRef, useState } from 'react';
import SwiperCore, { Navigation, A11y } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import classNames from 'classnames';

import Svg from '../../svg/svg.component';
import Typography from '../../typography/typography';
import { trackTabClick } from '../../../utils/analytics/button-analytics';

interface TabsNavigationProps {
  cqItemsOrder: string[];
  activeItemId: number;
  setActiveItemId: (id: number) => void;
  cqItems: Record<string, any>;
  accessibilityLabel: string;
  setSwiper: (swiper: SwiperCore) => void;
}

SwiperCore.use([Navigation, A11y]);

export const TabsNavigation: FC<TabsNavigationProps> = ({
  cqItemsOrder,
  activeItemId,
  setActiveItemId,
  cqItems,
  accessibilityLabel,
  setSwiper,
}) => {
  const [swiperEl, setSwiperEl] = useState<SwiperCore>();
  const prevEl = useRef<HTMLDivElement>(null);
  const nextEl = useRef<HTMLDivElement>(null);

  const handleOnSwiper = (swiper: SwiperCore) => {
    setSwiper(swiper);
    setSwiperEl(swiper);
  };

  const trackClick = (item: string) => {
    trackTabClick({ accessibilityLabel, title: cqItems[item as any]['cq:panelTitle'] });
  };

  const handleTabNavClick = (index: number, item: string) => {
    setActiveItemId(index);
    swiperEl?.slideTo(index);
    trackClick(item);
  };

  const handleKeyDown = (event: any, index: number, item: string) => {
    if (event.code !== 'Enter') {
      return;
    }

    setActiveItemId(index);
    trackClick(item);
  };

  return (
    <div data-testid="navigation" className="snowflake-navigation-container">
      <div ref={prevEl} className="snowflake-navigation-icon snowflake-prev-tab">
        <Svg name="arrow-icon" className="snowflake-navigation-arrow" />
      </div>
      <Swiper
        onSwiper={handleOnSwiper}
        navigation={{ prevEl: prevEl.current, nextEl: nextEl.current }}
        slideToClickedSlide={true}
        slidesPerView="auto"
        className="snowflake-tabs-navigation cmp-tabs__tablist"
        role="tablist"
        aria-label={accessibilityLabel}
        aria-multiselectable="false"
      >
        {cqItemsOrder.map((item, index) => (
          <SwiperSlide style={{ maxWidth: 'max-content' }} tag="span" key={item}>
            <span
              data-testid="nav-menu-item"
              onClick={() => handleTabNavClick(index, item)}
              onKeyDown={(e) => handleKeyDown(e, index, item)}
              data-cmp-hook-tabs="tab"
              role="tab"
              tabIndex={0}
              className={classNames('snowflake-tabs-navigation-item cmp-tabs__tab', {
                active: index === activeItemId,
                'cmp-tabs__tab--active': index === activeItemId,
              })}
            >
              <Typography variant="body1">{cqItems[item as any]['cq:panelTitle']}</Typography>
            </span>
          </SwiperSlide>
        ))}
      </Swiper>
      <div ref={nextEl} className="snowflake-navigation-icon snowflake-next-tab">
        <Svg name="arrow-icon" className="snowflake-navigation-arrow" />
      </div>
    </div>
  );
};
